import type { FC, FormEventHandler } from 'react';
import { useCallback, useState } from 'react';
import useTimer from '@customHooks/useTimer';

import { OtpInput, Picture } from '@ui';
import icons from '@icons';
import formatCourierNumber from '@utils/courierPhone';
import { twcls } from '@utils/twcls';
import { observer } from 'mobx-react';

interface AuthFormPhoneProps {
  userPhone: string;
  authViaSms: (data: { phone: string; code?: number }) => void;
  isCall: boolean;
  isTelegram?: boolean;
  onBack?: () => void;
  error?: string;
  onSmsFallback?: () => void;
  autoSubmit?: boolean;
  isBarMenuSheet?: boolean;
}

const {
  arrowIosLeft: { midGrey: arrowLeftMidGrey }
} = icons.Outline;

const formatTimer = (seconds: number): string =>
  `${Math.floor(seconds / 60)}:${String(Math.ceil(seconds % 60)).padStart(
    2,
    '0'
  )}`;

const SHEET_OUTLINE_BTN__CLS =
  'flex items-center justify-center w-full h-12 px-4 text-sm leading-[1.2] bg-transparent border border-solid rounded-lg cursor-pointer disabled:cursor-default disabled:text-[var(--ultra-dark-grey)] disabled:border-[var(--mid-grey)]';
const SHEET_SUBMIT_BTN__CLS =
  'flex items-center justify-center w-full h-12 px-4 text-sm leading-[1.2] text-white rounded-lg cursor-pointer bg-[var(--dark-red)] disabled:cursor-default disabled:bg-[var(--mid-grey)] disabled:text-[var(--ultra-dark-grey)]';

const AuthFormPhone: FC<AuthFormPhoneProps> = ({
  userPhone,
  authViaSms,
  isCall,
  isTelegram = false,
  onBack,
  error,
  onSmsFallback,
  autoSubmit,
  isBarMenuSheet
}) => {
  const [formData, setFormData] = useState({ code: { value: '', error: '' } });
  // shared 60s lock for resend and the SMS fallback
  const { timer, resetTimer } = useTimer(60);

  const getCode = useCallback(
    ({ code }) => {
      authViaSms({ phone: userPhone, code: code || +formData.code.value });
    },
    [authViaSms, formData.code.value, userPhone]
  );

  const onResend = useCallback(() => {
    resetTimer();
    getCode({ code: -1024 });
  }, [resetTimer, getCode]);

  const isBtnDisabled = formData.code.value.length !== 4;

  const onSubmit: FormEventHandler<HTMLFormElement> = e => {
    e.preventDefault();
    authViaSms({ phone: userPhone, code: +formData.code.value });
  };

  const onCodeChange = (value: string): void => {
    setFormData(prev => ({
      ...prev,
      code: { ...prev.code, value }
    }));
  };
  const onCodeComplete = autoSubmit
    ? (value: string) => authViaSms({ phone: userPhone, code: +value })
    : undefined;

  const heading = isCall ? (
    <>Введите последние 4 цифры номера, с которого поступит звонок</>
  ) : isTelegram ? (
    'Введите код из Telegram'
  ) : (
    'Введите код из SMS'
  );

  if (isBarMenuSheet) {
    const resendTitle = isCall
      ? 'Повторный запрос звонка'
      : 'Повторная отправка кода';
    return (
      <form onSubmit={onSubmit} className='flex flex-col gap-3 pt-10'>
        <h2 className='text-xl font-medium leading-6'>{heading}</h2>
        {!isCall && (
          <p className='text-sm leading-[18px] text-[var(--ultra-dark-grey)]'>
            На номер{' '}
            <span className='font-medium text-[var(--main-font-color)]'>
              {formatCourierNumber(userPhone)}
            </span>{' '}
            мы отправим{' '}
            {isTelegram ? 'код подтверждения в Telegram' : 'SMS с кодом'}
          </p>
        )}
        <OtpInput
          className='py-3'
          slotsClassName='flex justify-center gap-3'
          slotClassName='w-14 h-20 rounded-lg border-[var(--ultra-dark-grey)]'
          autoFocus
          value={formData.code.value}
          onChange={onCodeChange}
          onComplete={onCodeComplete}
          error={error}
        />
        <p
          className={twcls(
            'text-sm font-medium leading-[18px] text-center',
            timer <= 0 && 'invisible'
          )}
        >
          {resendTitle} через {formatTimer(timer)} сек.
        </p>
        <div className='flex flex-col gap-2 pt-1'>
          <button
            type='button'
            disabled={timer > 0}
            onClick={onResend}
            className={SHEET_OUTLINE_BTN__CLS}
          >
            {isCall ? 'Запросить повторный звонок' : 'Отправить код повторно'}
          </button>
          {onSmsFallback && (
            <button
              type='button'
              disabled={timer > 0}
              onClick={onSmsFallback}
              className={SHEET_OUTLINE_BTN__CLS}
            >
              Отправить код в СМС
            </button>
          )}
          <button
            type='submit'
            disabled={isBtnDisabled}
            className={SHEET_SUBMIT_BTN__CLS}
          >
            Войти
          </button>
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={onSubmit}>
      <div className='AuthForm__heading-row'>
        <Picture
          icon={arrowLeftMidGrey}
          onClick={onBack}
          height='24'
          width='24'
          className='picture-component picture-component--on-click'
        />
        <div className='AuthForm__heading'>{heading}</div>
      </div>
      {!isCall ? (
        <div className='AuthForm__phone-sent'>
          На номер{' '}
          <span className='AuthForm__phone-sent__medium'>
            {formatCourierNumber(userPhone)}
          </span>{' '}
          мы отправили{' '}
          {isTelegram ? 'код в Telegram' : 'SMS с кодом подтверждения'}.
        </div>
      ) : null}
      <OtpInput
        className='mb-6'
        autoFocus
        value={formData.code.value}
        onChange={onCodeChange}
        onComplete={onCodeComplete}
        error={error}
      />
      <button
        type='button'
        disabled={timer > 0}
        onClick={onResend}
        className='mb-3 AuthFormBtn AuthFormBtn--grey'
      >
        {isCall ? 'Запросить повторный звонок' : 'Отправить код повторно'}
        {timer > 0 && ` через ${timer} сек.`}
      </button>
      {onSmsFallback && (
        <button
          type='button'
          disabled={timer > 0}
          onClick={onSmsFallback}
          className='mb-3 AuthFormBtn AuthFormBtn--grey'
        >
          Отправить код в SMS{timer > 0 && ` через ${timer} сек.`}
        </button>
      )}
      <button
        type='submit'
        disabled={isBtnDisabled}
        className='AuthFormBtn AuthFormPhone__submit'
      >
        Войти
      </button>
    </form>
  );
};

export default observer(AuthFormPhone);
